<script setup lang="ts">
import SearchBar from './SearchBar.vue'
import { RestaurantAreas } from '~/lib/interfaces/restaurant'
import { GTMContentGroup } from '~/lib/models/gtm'

const isMobile = inject<Ref<boolean>>('isMobile')

const appStore = useAppStore()
const restaurantStore = useRestaurantStore()
const { trackPageView, trackOnCloseModal } = useGTM()
const { t } = useLocale()
const { canShowLupapSearch } = useLupap()

const mobileSearch = ref(false)
const searchBar = ref<InstanceType<typeof SearchBar> | null>(null)

const disableSearch = computed(() => {
  if (!appStore.initialFetchLoaded) return true
  return !restaurantStore.candidateArea
})

const onClose = () => {
  restaurantStore.closeChangeRestaurantModal()
  trackOnCloseModal()
}

const onMobileSearchChangeVisibility = () => {
  mobileSearch.value = !mobileSearch.value
}

const localizeMe = async () => {
  await searchBar.value?.searchFromLocation()
}

onMounted(async () => {
  if (!restaurantStore.areaSelected) {
    await restaurantStore.setArea({
      area: RestaurantAreas.MOP,
      updateGoogleMaps: true,
    })
  }
  trackPageView(
    {
      cg: GTMContentGroup.primary.ECOMMERCE,
      cg2: GTMContentGroup.secondary.LOCATOR,
    },
    true
  )
})
</script>

<template>
  <ModalCenter :add-close-button="false" :content-full="true" :mobile-full="false" @close="onClose">
    <template #header>
      <div class="p-2 md:p-0">
        <CloseIcon width="16" height="16" class="cursor-pointer" @click="onClose" />
      </div>
      <h3 class="pt-3 text-center text-2xl font-bold md:hidden">{{ t('home.searchTitle') }}</h3>
    </template>
    <template #content>
      <div class="flex flex-1 flex-col gap-4 md:mt-10 md:block md:flex-none md:px-14">
        <div class="hidden md:flex md:flex-col md:gap-4">
          <h2 class="block text-2xl font-bold">{{ t('home.searchTitle') }}</h2>
          <p class="block text-sm leading-4 tracking-[-0.15px] text-mcd-secondaryDarkGrey">
            {{ t('home.searchSubtitle', { pickup: t('common.MOP') }) }}
          </p>
        </div>
        <ToggleSearch
          :candidate-area="restaurantStore.candidateArea"
          :area-selected="restaurantStore.areaSelected"
          @set-area="restaurantStore.setArea"
        />

        <div>
          <LazySearchBar
            v-if="restaurantStore.candidateArea"
            ref="searchBar"
            :modal="true"
            :absolute-places="!isMobile"
            :open-search="isMobile || canShowLupapSearch"
            :search-screen="isMobile || canShowLupapSearch"
            :disable-search="disableSearch"
            @search-mobile="onMobileSearchChangeVisibility"
          />
        </div>
      </div>
      <LatestRestaurantsList v-if="restaurantStore.candidateArea === RestaurantAreas.MOP" />
      <LatestAddressesList v-if="restaurantStore.candidateArea === RestaurantAreas.DLV" />

      <!-- LocateMe btn on mobile -->
      <div class="my-8 block px-4 md:hidden">
        <McdButton is-primary class="w-full py-3 text-sm" @on-click="localizeMe">
          <div class="flex flex-row items-center justify-center gap-2">
            <img src="/images/icons/locateMe.svg" alt="locateMe" />
            <span class="block">{{ t('home.searchLocateMe') }}</span>
          </div>
        </McdButton>
      </div>
    </template>
  </ModalCenter>

  <template v-if="mobileSearch">
    <LazySearchLupap v-if="canShowLupapSearch" @close="onMobileSearchChangeVisibility" />
    <LazySearchMobileModal v-else @close="onMobileSearchChangeVisibility" />
  </template>
</template>
